import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@parentpath/i18n';
import { useFAQSearch } from '../hooks/useFAQ';

interface FAQSearchBarProps {
  onSelectResult?: (result: { id: string; question: string }) => void;
}

export function FAQSearchBar({ onSelectResult }: FAQSearchBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [searchPosition, setSearchPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation('help');

  const { data: results, isLoading } = useFAQSearch(query);

  // Track search container position for portal rendering
  useLayoutEffect(() => {
    if (placeholderRef.current && isOpen) {
      const rect = placeholderRef.current.getBoundingClientRect();
      setSearchPosition({ top: rect.top, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  // Update position on scroll/resize when search is active
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (placeholderRef.current) {
        const rect = placeholderRef.current.getBoundingClientRect();
        setSearchPosition({ top: rect.top, left: rect.left, width: rect.width });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (result: { id: string; question: string; category_slug: string }) => {
    setIsOpen(false);
    setQuery('');
    if (onSelectResult) {
      onSelectResult(result);
    } else {
      // Navigate to category with highlight
      navigate(`/help/category/${result.category_slug}#${result.id}`);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Search container content - reused in normal and portal rendering
  const searchContainerContent = (
    <div className="relative w-full">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(e.target.value.length >= 2);
          }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          placeholder={t('search.placeholder')}
          className="w-full pl-12 pr-12 py-4 rounded-[15px]
                     bg-white/10 border border-white/20
                     text-white placeholder-white/40 caret-white
                     focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                     outline-none transition-all text-base"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1
                       hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-white/40" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 glass-effect rounded-[15px]
                     border border-white/25 shadow-lg max-h-80 overflow-y-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center">
              <div className="animate-spin h-5 w-5 border-2 border-pp-orange border-t-transparent rounded-full mx-auto" />
            </div>
          ) : results && results.length > 0 ? (
            <ul className="divide-y divide-white/10">
              {results.map((result) => (
                <li key={result.id}>
                  <button
                    onClick={() => handleSelect({
                      id: result.id,
                      question: result.question,
                      category_slug: result.category_slug
                    })}
                    className="w-full text-left p-4 hover:bg-white/10 transition-colors
                               flex items-start gap-3"
                  >
                    <ArrowRight className="h-5 w-5 text-pp-orange flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white line-clamp-1">
                        {result.question}
                      </p>
                      <p className="text-sm text-white/50">
                        {result.category_name}
                        {result.subcategory_name && ` → ${result.subcategory_name}`}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-white/50">
              {t('search.noResults', { query })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Search Backdrop + Container - rendered via portal for iOS z-index fix */}
      {isOpen && searchPosition && createPortal(
        <>
          <div
            className="fixed inset-0 bg-pp-dark/60 backdrop-blur-sm z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[9999]"
            style={{
              top: searchPosition.top,
              left: searchPosition.left,
              width: searchPosition.width,
            }}
          >
            {searchContainerContent}
          </div>
        </>,
        document.body
      )}

      {/* Placeholder when portal is active, normal search bar when not */}
      <div
        ref={placeholderRef}
        className={isOpen ? 'invisible' : ''}
      >
        {searchContainerContent}
      </div>
    </>
  );
}
